import ShortUniqueId from "short-unique-id";
/**
 * Recursively traverses and optionally transforms a Portable Text structure using a provided
 * callback function. The callback is applied to each node in the structure. If the callback
 * does not modify a node, the original node is used.
 *
 * @template T The type of the Portable Text nodes, defaulting to PortableTextObject.
 * @param {T} object - The root node of the Portable Text structure to be traversed.
 *   It can be a default Portable Text object or a custom type that extends from it.
 * @param {(object: T) => ArbitraryTypedObject | undefined} callback - A callback function
 *   invoked for each node in the Portable Text structure. It can return a modified version
 *   of the node or `undefined` if no modifications are to be made.
 * @returns {ArbitraryTypedObject} - A modified copy of the original portable text structure.
 */
export const traversePortableText = (object, callback) => {
    // ensure a deep copy is created instead of modifying the original object
    const traversedObject = callback(object) ?? { ...object };
    Object.keys(traversedObject).forEach((key) => {
        // marks is an array of strings that shouldn't be modified, therefore omit from traversal
        if (Array.isArray(traversedObject[key]) && key !== "marks") {
            traversedObject[key] = traversedObject[key].map((child) => traversePortableText(child, callback));
        }
    });
    return traversedObject;
};
export const createSpan = (guid, marks, text) => ({
    _type: "span",
    _key: guid,
    marks: marks || [],
    text: text || "",
});
export const createBlock = (guid, markDefs, style, children) => ({
    _type: "block",
    _key: guid,
    markDefs: markDefs || [],
    style: style || "normal",
    children: children || [],
});
export const createListBlock = (guid, level, listItem, markDefs, style, children) => ({
    _type: "block",
    _key: guid,
    markDefs: markDefs || [],
    level: level,
    listItem: listItem,
    style: style || "normal",
    children: children || [],
});
export const createImageBlock = (guid, reference, url, alt) => ({
    _type: "image",
    _key: guid,
    asset: {
        _type: "reference",
        _ref: reference,
        url,
        alt,
    },
});
export const createTableBlock = (guid, columns) => ({
    _type: "table",
    _key: guid,
    numColumns: columns,
    rows: [],
});
export const createExternalLink = (guid, attributes) => ({
    _key: guid,
    _type: "link",
    ...attributes,
});
export const createItemLink = (guid, reference) => ({
    _key: guid,
    _type: "internalLink",
    reference: {
        _type: "reference",
        _ref: reference,
    },
});
export const createTable = (guid, numColumns) => ({
    _key: guid,
    _type: "table",
    numColumns: numColumns,
    rows: [],
});
export const createTableRow = (guid) => ({
    _key: guid,
    _type: "row",
    cells: [],
});
export const createTableCell = (guid, childCount) => ({
    _key: guid,
    _type: "cell",
    content: [],
    childBlocksCount: childCount,
});
export const createMark = (guid, value, childCount) => ({
    _type: "mark",
    _key: guid,
    value: value,
    childCount: childCount,
});
export const createComponentBlock = (guid, reference, dataType) => ({
    _type: "component",
    _key: guid,
    dataType,
    component: reference,
});
export const compose = (firstFunction, ...functions) => functions.reduce((previousFunction, nextFunction) => (value) => previousFunction(nextFunction(value)), firstFunction);
export const getAllNewLineAndWhiteSpace = /\n\s*/g;
export const { randomUUID } = new ShortUniqueId({ length: 10 });
